/**
 * Which glossary terms get a diagram, and which measurement it draws.
 *
 * ── Why this is one map and not a field on the entry ─────────────────────────
 *
 * The choice is about geometry, not about language: the diagram for "gonial
 * angle" is the same drawing in all five bundles. Putting it on `GlossaryEntry`
 * would mean the same decision written out five times, and the failure mode is
 * a term that quietly loses its picture in Portuguese because one file was
 * edited and four were not.
 *
 * ── Why most terms are absent, and why that is the point ─────────────────────
 *
 * A term appears here only when a front-facing projection can show it honestly.
 * `e-line`, `ogee-curve`, `orbital-vector` and `submental-cervical-angle` are
 * profile measurements — their own entries say so at length — and the only way
 * to illustrate them here would be to invent the geometry on the pages that
 * exist to separate what is measured from what is asserted. They keep their
 * prose.
 *
 * `bone-smashing`, `mogging`, `halo-effect`, `looksmaxxing` and `psl-score` have
 * no geometry at all. `maxilla` and `buccal-fat` are anatomy the landmark model
 * does not locate. A diagram on any of them would be decoration standing in for
 * an explanation.
 */

import type { OverlayKey } from "../../components/tools/MeasurementOverlay";
import type { GlossarySlug } from "./types";

export const GLOSSARY_DIAGRAM: Partial<Record<GlossarySlug, OverlayKey>> = {
  // The eye-corner angle itself, and the four terms that are that angle under
  // other names or with a sign attached.
  "canthal-tilt": "canthalTilt",
  "positive-canthal-tilt": "canthalTilt",
  "negative-canthal-tilt": "canthalTilt",
  "hunter-eyes": "canthalTilt",
  "prey-eyes": "canthalTilt",
  "upper-eyelid-exposure": "canthalTilt",

  // The jaw corner: the two rays whose interior angle IS the gonial angle,
  // plus the jaw width the ratio uses.
  "gonial-angle": "jawline",
  "jawline-score": "jawline",
  mewing: "jawline",
  "mewing-chart": "jawline",

  "face-symmetry": "symmetry",
  "golden-ratio": "goldenRatio",
  fwhr: "fwhr",
  "facial-thirds": "thirds",
  "midface-ratio": "thirds",
  "intercanthal-distance": "eyeSpacing",
  philtrum: "lipRatio",
  "face-shape-test": "faceShape",
  neoteny: "faceShape",
  "sexual-dimorphism": "fwhr",
};
